import React, { useCallback, useRef, useState } from "react";
import openNewTab from "../../../lib/openNewTab";
import { wait } from "../../../lib/wait";
import modelApi from "../../../services/modelApi";
import Banner from "../../atoms/banner/Banner";
import Button from "../../atoms/button/Button";
import Text from "../../atoms/text/Text";
import InlineGroup from "../../atoms/inlinegroup/InlineGroup";
import VerticalGroup from "../../atoms/verticalgroup/VerticalGroup";
import CloseRoundModal from "./CloseRoundModal";
import Icon from "../../atoms/icon/Icon";
import { usePollingModelConfiguration } from "../../../hooks/useModelConfiguration";
import PreviewReportingModal from "./PreviewReportingModal";
import UploadResultsModal from "./UploadResultsModal";
import openNewWindow from "../../../lib/openNewWindow";

interface UIState {
  closeModalOpen: boolean;
  previewModalOpen: boolean;
  uploadResultsModalOpen: boolean;
}
interface Props {
  closeRound: () => Promise<void>;
  totalRounds: number;
  gameId: string;
  round: number;
}

const statuses = [
  "initialising",
  "initialised",
  "round-calculating",
  "round-calculated",
  "round-sculpting",
  "round-sculpted",
  "reporting-calculating",
  "reporting-calculated",
  "reporting-publishing",
  "reporting-published",
  "results-uploaded",
];

const isPastDesiredStatus = ({
  modelConfig,
  desiredStatus,
  round,
}: {
  modelConfig: ModelAPI.ConfigurationResponse | null;
  desiredStatus: ModelAPI.ModelState;
  round: number;
}) =>
  modelConfig != null &&
  modelConfig.currentRound === round &&
  statuses.indexOf(modelConfig.state) >= statuses.indexOf(desiredStatus);

const Status = ({
  modelConfig,
  desiredStatus,
  label,
  round,
}: {
  modelConfig: ModelAPI.ConfigurationResponse | null;
  desiredStatus: ModelAPI.ModelState;
  label: string;
  round: number;
}) => {
  const completed = isPastDesiredStatus({ modelConfig, desiredStatus, round });
  return (
    <InlineGroup spaceBetweenElements={2}>
      <Icon
        noMargin
        type="status"
        colour={completed ? "lightgreen" : "grey1"}
      />
      <Text>{label}</Text>
    </InlineGroup>
  );
};

function CloseRoundScreen({ closeRound, gameId, totalRounds, round }: Props) {
  const [
    { closeModalOpen, previewModalOpen, uploadResultsModalOpen },
    setUIState,
  ] = useState<UIState>({
    closeModalOpen: false,
    previewModalOpen: false,
    uploadResultsModalOpen: false,
  });
  const [inProgress, setInprogress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: modelConfig } = usePollingModelConfiguration(gameId, 5000);
  const configRef = useRef<ModelAPI.ConfigurationResponse | null>();
  configRef.current = modelConfig;

  const calculateResults = useCallback(async () => {
    try {
      setError(null);
      setInprogress(true);
      await modelApi.recalculateAll(gameId, round);
      await wait(5000);
      let iteration = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        iteration++;
        if (
          configRef.current?.state === "reporting-calculated" &&
          configRef.current?.currentRound === round
        ) {
          break;
        }
        if (iteration > 100) {
          throw new Error("Timed out sculpting");
        }
        await wait(2000);
      }
      await wait(5000);
      setInprogress(false);
    } catch (e) {
      setError(e.message);
      setInprogress(false);
    }
  }, [gameId, round]);

  const publishReporting = useCallback(async () => {
    try {
      setError(null);
      setInprogress(true);
      await modelApi.publishReporting(gameId, round);
      let iteration = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        iteration++;
        if (configRef.current?.state === "reporting-published") {
          break;
        }
        if (iteration > 100) {
          throw new Error("Timed out publishing");
        }
        await wait(2000);
      }
      setInprogress(false);
    } catch (e) {
      setError(e.message);
      setInprogress(false);
    }
  }, [gameId, round]);

  const uploadResults = useCallback(async () => {
    try {
      setError(null);
      setInprogress(true);
      await modelApi.uploadResults(gameId, round + 1);
      let iteration = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        iteration++;
        if (configRef.current?.state === "results-uploaded") {
          break;
        }
        if (iteration > 100) {
          throw new Error("Timed out uploading");
        }
        await wait(2000);
      }
      setInprogress(false);
      setUIState((prevState) => ({
        ...prevState,
        uploadResultsModalOpen: false,
      }));
    } catch (e) {
      setError(e.message);
      setInprogress(false);
    }
  }, [gameId, round]);

  const onPreviewClose = useCallback(() => {
    setUIState((prevState) => ({ ...prevState, previewModalOpen: false }));
  }, [setUIState]);

  const canProceed =
    modelConfig != null &&
    modelConfig.currentRound === round &&
    statuses.indexOf(modelConfig.state) >=
      statuses.indexOf("reporting-published");

  return (
    <>
      {closeModalOpen && (
        <CloseRoundModal
          isOpen={closeModalOpen}
          onClose={() =>
            setUIState((prevState) => ({ ...prevState, closeModalOpen: false }))
          }
          onComplete={closeRound}
          totalRounds={totalRounds}
          round={round}
        />
      )}
      {uploadResultsModalOpen && (
        <UploadResultsModal
          isOpen={uploadResultsModalOpen}
          onClose={() =>
            setUIState((prevState) => ({
              ...prevState,
              uploadResultsModalOpen: false,
            }))
          }
          onComplete={uploadResults}
        />
      )}
      {previewModalOpen && (
        <PreviewReportingModal
          eventId={gameId}
          round={round}
          isOpen={previewModalOpen}
          onClose={onPreviewClose}
        />
      )}
      {error && <Banner type="error" message={error} active />}

      <VerticalGroup spaceBetweenElements={2} wide>
        {round <= totalRounds &&
          inProgress &&
          modelConfig?.currentRound === round && (
            <Text>{modelConfig?.progressMessage}</Text>
          )}
        {round <= totalRounds && (
          <>
            <Status
              label="Round Calculated"
              modelConfig={modelConfig}
              round={round}
              desiredStatus="round-calculated"
            />
            <Status
              label="Round Sculpted"
              modelConfig={modelConfig}
              round={round}
              desiredStatus="round-sculpted"
            />
            <Status
              label="Reporting Generated"
              modelConfig={modelConfig}
              round={round}
              desiredStatus="reporting-calculated"
            />
            <Status
              label="Reporting Published"
              modelConfig={modelConfig}
              round={round}
              desiredStatus="reporting-published"
            />
            <Status
              label="Results Uploaded to Game API (optional)"
              modelConfig={modelConfig}
              round={round}
              desiredStatus="results-uploaded"
            />
          </>
        )}
        {round <= totalRounds && (
          <Button
            block
            onClick={calculateResults}
            outline
            inProgress={inProgress}
          >
            Calculate Results
          </Button>
        )}
        {round <= totalRounds && (
          <Button
            disabled={
              !isPastDesiredStatus({
                modelConfig,
                desiredStatus: "reporting-calculated",
                round,
              })
            }
            block
            onClick={() =>
              setUIState((state) => ({ ...state, previewModalOpen: true }))
            }
            outline
          >
            Preview Results Slides
          </Button>
        )}
        {round <= totalRounds && (
          <Button
            disabled={
              !isPastDesiredStatus({
                modelConfig,
                desiredStatus: "reporting-calculated",
                round,
              })
            }
            block
            onClick={() => {
              openNewTab(
                `${window.location.origin}/results/games/${gameId}/rounds/${round}/presenter`,
              );
              openNewWindow(
                `${window.location.origin}/results/games/${gameId}/rounds/${round}/presentation`,
              );
            }}
            outline
          >
            Present Results Slides
          </Button>
        )}
        {round <= totalRounds && (
          <Button
            disabled={
              !isPastDesiredStatus({
                modelConfig,
                desiredStatus: "reporting-calculated",
                round,
              })
            }
            block
            onClick={publishReporting}
            outline
          >
            Publish Results Slides
          </Button>
        )}
        {round <= totalRounds && (
          <Button
            disabled={
              !isPastDesiredStatus({
                modelConfig,
                desiredStatus: "reporting-published",
                round,
              })
            }
            block
            onClick={() =>
              setUIState((state) => ({
                ...state,
                uploadResultsModalOpen: true,
              }))
            }
            outline
          >
            Upload Results
          </Button>
        )}
        {round <= totalRounds && (
          <Button
            disabled={!canProceed}
            block
            onClick={() =>
              setUIState((state) => ({ ...state, closeModalOpen: true }))
            }
            colour="blue"
          >
            Lock and proceed to the next round
          </Button>
        )}
      </VerticalGroup>
    </>
  );
}

export default CloseRoundScreen;
